import { useState } from "react";
import { Check, Circle } from "lucide-react";
import { home } from "../content/home";
import { cn } from "../lib/cn";
import { LiveCardsScreen } from "./LiveCardsScreen";
import { NotesScreen } from "./NotesScreen";

const { label, headline, steps, note } = home.howItWorks;

// The step whose tablet shows the live sidebar ("The work shows up as you talk.").
const LIVE_STEP = 3;
// The step whose tablet shows the meeting notes ("The notes are done when you hang up.").
const NOTES_STEP = 4;

const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-paper";

export function HowItWorksSection() {
  const [active, setActive] = useState(0);

  return (
    <section id="how-it-works" aria-label={label} className="scroll-mt-20 px-4 py-24 sm:px-6 lg:py-32">
      <div className="mx-auto grid max-w-6xl items-center gap-16 lg:grid-cols-[0.85fr_1.15fr] lg:gap-14">
        <div>
          <h2 className="font-serif text-[clamp(2.5rem,5vw,3.75rem)] font-medium leading-[0.98] tracking-[-0.015em] text-char">
            {headline.line1}
            <br />
            <em className="text-brand-500">{headline.line2}</em>
          </h2>

          <ol className="mt-10 border-l border-char/15">
            {steps.map((step, i) => {
              const open = i === active;
              return (
                <li key={step.title} className="relative">
                  <span
                    aria-hidden="true"
                    className={cn(
                      "absolute -left-px top-0 h-full w-0.5 transition-colors",
                      open ? "bg-brand-500" : "bg-transparent",
                    )}
                  />
                  <h3>
                    <button
                      type="button"
                      aria-expanded={open}
                      onClick={() => setActive(i)}
                      className={cn("flex w-full items-baseline gap-4 rounded py-3 pl-6 text-left", focusRing)}
                    >
                      <span
                        aria-hidden="true"
                        className={cn("font-serif text-xl italic", open ? "text-brand-600" : "text-char/35")}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span
                        className={cn(
                          "font-serif text-[22px] leading-tight transition-colors",
                          open ? "text-char" : "text-char/55 hover:text-char",
                        )}
                      >
                        {step.title}
                      </span>
                    </button>
                  </h3>
                  {open && (
                    <p className="pb-4 pl-[61px] pr-2 font-sans text-base leading-relaxed text-char/75">
                      {step.body}
                    </p>
                  )}
                </li>
              );
            })}
          </ol>
        </div>

        <div className="relative px-2 pb-10 sm:px-8">
          <div aria-hidden="true" className="absolute inset-x-0 inset-y-6 -rotate-6 rounded-[28px] border border-char bg-brand-500 sm:inset-x-4" />
          <div className="relative rotate-2 rounded-[26px] border border-char bg-char p-2.5 shadow-offset sm:p-3.5">
            <div
              role="img"
              aria-label={`Intakall screen for step ${active + 1}: ${steps[active].title}`}
              className="min-h-[260px] overflow-hidden rounded-[18px] bg-paper-raised sm:aspect-[16/11] sm:min-h-0"
            >
              <div
                key={active}
                className={cn(
                  "h-full font-sans text-char motion-safe:animate-screen-in",
                  // Steps 4 and 5 are real Intakall screens, edge to edge.
                  active === LIVE_STEP ? "bg-[#1a1a2e]" : active === NOTES_STEP ? "" : "p-5 sm:p-7",
                )}
              >
                <TabletScreen step={active} />
              </div>
            </div>
          </div>
          <p className="absolute -bottom-2 right-4 rotate-[-4deg] font-hand text-2xl text-char/80 sm:right-10">
            {note}
          </p>
        </div>
      </div>
    </section>
  );
}

function ScreenTitle({ children }: { children: string }) {
  return <p className="mb-4 font-mono text-[11px] uppercase tracking-wider text-char/50">{children}</p>;
}

function Row({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("flex items-center justify-between gap-3 rounded-xl border border-char/15 bg-white px-4 py-3", className)}>
      {children}
    </div>
  );
}

// Illustrative screens, one per step. Decorative detail only: the step text is
// the real content, and the wrapper's aria-label names the step.
function TabletScreen({ step }: { step: number }) {
  switch (step) {
    case 0:
      return (
        <div className="flex h-full flex-col items-center justify-center text-center">
          <img src="/mark.svg" alt="" className="h-12 w-12" />
          <p className="mt-4 font-serif text-3xl">Intakall is ready</p>
          <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-char/70">
            <Check className="h-4 w-4 text-brand-600" aria-hidden="true" /> Signed in with Google
          </p>
          <p className="mt-8 max-w-xs rounded-xl border border-char/15 bg-white px-4 py-3 text-sm text-char/80">
            Open your next meeting. The Intakall sidebar is waiting on the right.
          </p>
        </div>
      );
    case 1:
      return (
        <div>
          <ScreenTitle>Your team's tools</ScreenTitle>
          <div className="space-y-2">
            {home.integrations.tools.map((tool, i) => (
              <Row key={tool.name}>
                <span className="inline-flex items-center gap-3 text-sm font-semibold">
                  <img src={tool.logo} alt="" className="h-5 w-5" />
                  {tool.name}
                </span>
                {i < 3 ? (
                  <span className="text-xs font-semibold text-brand-700">Connected</span>
                ) : (
                  <span className="rounded-lg border border-char px-2.5 py-1 text-xs font-semibold">Connect</span>
                )}
              </Row>
            ))}
          </div>
        </div>
      );
    case 2:
      return (
        <div className="flex h-full flex-col items-center justify-center text-center">
          <ScreenTitle>Weekly sync</ScreenTitle>
          <span className="inline-flex items-center gap-2.5 rounded-full border border-char bg-[#e5484d] px-6 py-3 text-base font-semibold text-white shadow-offset-sm">
            <Circle className="h-3 w-3 fill-white" aria-hidden="true" /> Recording
          </span>
          <p className="mt-4 text-sm text-char/70">Started by you · 3 teammates in the call</p>
        </div>
      );
    case LIVE_STEP:
      return <LiveCardsScreen />;
    default:
      return <NotesScreen />;
  }
}
